import { useCallback, useEffect, useRef, useState } from 'react';
import { readOpeningHours, type ReadOpeningHoursOutcome } from '@repo/front-end-services';
import type { LaunchHours } from '@repo/frontend-types';

/**
 * Whether the twin's opening hours let it launch, as the middleware's one rule answers it
 * (`launchHours` on the hours read; spec twin-details-page-launch-gate, R5). Read at page level so
 * the Launch step holds *Go live* on it and the Opening hours step's tick follows the same state on
 * every step. The Opening hours step adopts every answer its own reads and saves get
 * (`twin-details-page-hours-week`, R3), so the tick moves with each save.
 *
 * `failed` is "we could not find out" — a failed read, a failed token, no twin BID — and holds
 * *Go live* exactly as not-ready does: "could not check" is not permission (ruled 2026-10-16).
 */
export type LaunchHoursState =
  | { read: 'pending' }
  | { read: 'ready'; launchHours: LaunchHours }
  | { read: 'failed' };

/** The one predicate: Launch's summary tick, *Go live*'s hold and the Opening hours step's tick. */
export const openingHoursReady = (state: LaunchHoursState): boolean =>
  state.read === 'ready' && state.launchHours.ready;

const PENDING: LaunchHoursState = { read: 'pending' };
const FAILED: LaunchHoursState = { read: 'failed' };

interface Params {
  middlewareApi: string;
  /** Fetched once, after mount. Not here YET is `pending`; `tokenFailed` is `failed`. */
  accessToken: string | null;
  tokenFailed: boolean;
  /** The page's `?twinBid=`. None is `failed`, never an endless `pending`. */
  twinBid: string | null;
  /** True while the Launch step is showing: each time it opens, the hours are read again. */
  active: boolean;
}

export interface UseLaunchHours {
  state: LaunchHoursState;
  /**
   * Whether a read can be made at all: a token and a twin BID. Without them *Check again* would do
   * nothing, so the page offers it only when this is true.
   */
  canRead: boolean;
  /** True while a *Check again* read is in flight; the state on screen stays as it was. */
  rechecking: boolean;
  /** Read again: *Check again*. */
  refresh: () => void;
  /**
   * Show an answer the server gave elsewhere: a refused launch claim's, and every answer the Opening
   * hours step's reads and saves get — so the page, the claim and the step agree.
   */
  adopt: (launchHours: LaunchHours) => void;
  /** The claim could not read the hours: show "Couldn't check". */
  markUnchecked: () => void;
}

export const useLaunchHours = ({
  middlewareApi,
  accessToken,
  tokenFailed,
  twinBid,
  active,
}: Params): UseLaunchHours => {
  const [state, setState] = useState<LaunchHoursState>(PENDING);
  const [rechecking, setRechecking] = useState(false);
  /**
   * Which answer owns the state. A read takes a number when it starts; `adopt`, `markUnchecked`, a
   * twin change and unmount take one too, so a slow read that started earlier never overwrites a
   * newer answer — or answers for a twin the page has left.
   */
  const seqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  /** The twin the first read was made for, so the first read happens whatever step is showing. */
  const readForRef = useRef<string | null>(null);

  const claimView = useCallback(() => {
    seqRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    return seqRef.current;
  }, []);

  const read = useCallback(
    async (recheck: boolean): Promise<void> => {
      if (!accessToken || !twinBid) return;
      const seq = claimView();
      const controller = new AbortController();
      abortRef.current = controller;
      if (recheck) setRechecking(true);
      // The client never throws by contract. A throw anyway — a broken mock, or a stale package
      // build without this export, which throws before any promise exists — is failed, never an
      // endless pending.
      let outcome: ReadOpeningHoursOutcome;
      try {
        outcome = await readOpeningHours(middlewareApi, accessToken, twinBid, controller.signal);
      } catch {
        outcome = { status: 'failed' };
      }
      if (seq !== seqRef.current) return;
      abortRef.current = null;
      setRechecking(false);
      setState(
        outcome?.status === 'ready'
          ? { read: 'ready', launchHours: outcome.hours.launchHours }
          : FAILED,
      );
    },
    [accessToken, middlewareApi, twinBid, claimView],
  );

  // A different twin starts again; no twin or a failed token can never be read, so it is failed.
  useEffect(() => {
    claimView();
    readForRef.current = null;
    setRechecking(false);
    setState(!twinBid || tokenFailed ? FAILED : PENDING);
  }, [twinBid, tokenFailed, claimView]);

  // The first read for a twin, on any step; then again each time the Launch step opens.
  useEffect(() => {
    if (!accessToken || !twinBid || tokenFailed) return;
    if (!active && readForRef.current === twinBid) return;
    readForRef.current = twinBid;
    void read(false);
  }, [active, accessToken, twinBid, tokenFailed, read]);

  useEffect(
    () => () => {
      claimView();
    },
    [claimView],
  );

  const refresh = useCallback(() => {
    void read(true);
  }, [read]);

  const adopt = useCallback(
    (launchHours: LaunchHours) => {
      claimView();
      setRechecking(false);
      setState({ read: 'ready', launchHours });
    },
    [claimView],
  );

  const markUnchecked = useCallback(() => {
    claimView();
    setRechecking(false);
    setState(FAILED);
  }, [claimView]);

  const canRead = !!accessToken && !!twinBid && !tokenFailed;

  return { state, canRead, rechecking, refresh, adopt, markUnchecked };
};
